import { useCallback, useMemo } from 'react';
import { authClient } from '@/lib/auth-client';
import { useLocalStorage } from 'usehooks-ts';
import { trpc } from '@/lib/trpc';
import { ADMIN_VIEW_KEY, DEBUG_MODE_KEY } from '@/lib/consts';

export function useCurrentUser() {
  const [adminViewEnabled, setAdminViewEnabled] = useLocalStorage(ADMIN_VIEW_KEY, true);
  const [debugModeEnabled, setDebugMode] = useLocalStorage(DEBUG_MODE_KEY, false);

  const query = trpc.auth.me.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const user = query.data?.user ?? null;
  const isAuthenticated = !!user;
  const isStackAdmin = user?.isStackAdmin ?? false;
  const isOrgAdmin = user?.orgRole === 'admin' || user?.orgRole === 'owner' || isStackAdmin;

  const sessionStatus = query.isLoading
    ? 'loading'
    : isAuthenticated
      ? 'authenticated'
      : 'unauthenticated';

  return {
    // User data
    user,

    // Loading state
    isLoading: query.isLoading,
    sessionStatus,
    isAuthenticated,

    // Permissions (derived)
    isOrgAdmin,
    isStackAdmin,
    isAdminViewActive: isOrgAdmin && adminViewEnabled,
    orgRole: user?.orgRole ?? null,

    // Debug/admin toggles
    adminViewEnabled,
    debugMode: isStackAdmin && debugModeEnabled,
    setAdminViewEnabled,
    setDebugMode,
  };
}

export function useOrgSwitcher() {
  const utils = trpc.useUtils();
  const { data: orgs, isLoading } = useOrganizations();
  const { user } = useCurrentUser();

  const switchMutation = trpc.org.switchOrg.useMutation({
    onSuccess: () => {
      // Invalidate user data
      utils.auth.me.invalidate();
      utils.org.listMyOrgs.invalidate();

      // Invalidate org-scoped data (selective, not everything)
      utils.chat.listWorkspaces.invalidate();
      utils.memories.list.invalidate();
      utils.connectors.list.invalidate();
    },
  });

  const switchOrg = useCallback(
    (orgId: string) => switchMutation.mutateAsync({ organizationId: orgId }),
    [switchMutation]
  );

  const memberships = orgs?.memberships ?? [];
  const currentOrg = useMemo(
    () => memberships.find((m) => m.organizationId === user?.organizationId) ?? null,
    [memberships, user?.organizationId]
  );

  return {
    organizations: memberships,
    currentOrg,
    switchOrg,
    isLoading,
    isSwitching: switchMutation.isPending,
  };
}

function useOrganizations() {
  const { isAuthenticated } = useCurrentUser();

  return trpc.org.listMyOrgs.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInvalidateUser() {
  const utils = trpc.useUtils();

  return useCallback(() => {
    utils.auth.me.invalidate();
    utils.org.listMyOrgs.invalidate();
  }, [utils]);
}

/**
 * Debug hook for inspecting auth state - includes raw Better Auth session.
 * Only use this for debugging/dev tools, not for app logic.
 */
export function useUserDebugData() {
  // Raw Better Auth session (for debugging only)
  const { data: session, isPending: isSessionLoading } = authClient.useSession();

  // tRPC queries
  const userQuery = trpc.auth.me.useQuery();
  const orgsQuery = trpc.org.listMyOrgs.useQuery(undefined, {
    enabled: !!userQuery.data?.user,
  });

  const {
    isOrgAdmin,
    isStackAdmin,
    isAdminViewActive,
    adminViewEnabled,
    debugMode,
    sessionStatus,
  } = useCurrentUser();

  return {
    session: {
      status: isSessionLoading ? 'loading' : session ? 'authenticated' : 'unauthenticated',
      user: session?.user ?? null,
      expiresAt: session?.session?.expiresAt ?? null,
    },

    authMe: {
      loading: userQuery.isLoading,
      status: sessionStatus,
      error: userQuery.error?.message ?? null,
      data: userQuery.data ?? null,
    },

    // Organization memberships
    organizations: {
      loading: orgsQuery.isLoading,
      error: orgsQuery.error?.message ?? null,
      data: orgsQuery.data ?? null,
    },

    // Derived permissions
    permissions: {
      isOrgAdmin,
      isStackAdmin,
      isAdminViewActive,
      adminViewEnabled,
      debugMode,
    },

    // Timestamps for debugging staleness
    meta: {
      timestamp: new Date().toISOString(),
      authMeDataUpdatedAt: userQuery.dataUpdatedAt
        ? new Date(userQuery.dataUpdatedAt).toISOString()
        : null,
      orgsDataUpdatedAt: orgsQuery.dataUpdatedAt
        ? new Date(orgsQuery.dataUpdatedAt).toISOString()
        : null,
    },
  };
}
